import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/**
 * Fetches avatar URLs for ForkSeek-linked candidates.
 * Joins candidates → candidate_profiles via seek_candidate_number = candidate_number.
 * Returns a Map<candidateId, avatarUrl>.
 */
export function useCandidateAvatars(candidateIds: string[]) {
  return useQuery({
    queryKey: ['candidate-avatars', candidateIds.sort().join(',')],
    queryFn: async () => {
      if (!candidateIds.length) return new Map<string, string>();

      // Query candidate_profiles for matching seek_candidate_numbers
      const { data, error } = await supabase
        .from('candidates')
        .select('id, seek_candidate_number')
        .in('id', candidateIds)
        .not('seek_candidate_number', 'is', null);

      if (error) throw error;
      if (!data?.length) return new Map<string, string>();

      const seekNumbers = data.map(c => c.seek_candidate_number!);

      const { data: profiles, error: pError } = await supabase
        .from('candidate_profiles')
        .select('candidate_number, avatar_url')
        .in('candidate_number', seekNumbers)
        .not('avatar_url', 'is', null);

      if (pError) throw pError;

      const numberToAvatar = new Map<number, string>();
      for (const p of profiles || []) {
        if (p.avatar_url) numberToAvatar.set(p.candidate_number, p.avatar_url);
      }

      const result = new Map<string, string>();
      for (const c of data) {
        const avatar = numberToAvatar.get(c.seek_candidate_number!);
        if (avatar) result.set(c.id, avatar);
      }
      return result;
    },
    enabled: candidateIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });
}
